import React, { useEffect, useState } from "react";
import Footer from "../Component/Footer";
import Header from "../Component/Header";
import { Users, Target, Heart, ArrowRight, BookOpen, GraduationCap, Building, Award, Briefcase, Star, Factory, DollarSign } from "lucide-react";

function Book() {
  const [isVisible, setIsVisible] = useState({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({
              ...prev,
              [entry.target.id]: true
            }));
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const projectHighlights = [
    {
      icon: GraduationCap,
      title: "3-Month Training",
      description: "Comprehensive vocational skills course in bookmaking and entrepreneurship",
      color: "from-blue-500 to-cyan-500",
      metric: "3 Months"
    },
    {
      icon: Users,
      title: "230 Beneficiaries",
      description: "Successfully trained youths and adolescent mothers from Kayunga District",
      color: "from-green-500 to-emerald-500",
      metric: "230 Trained"
    },
    {
      icon: Factory,
      title: "Manufacturing Plant",
      description: "Hands-on training facility located in Kayunga Central for practical learning",
      color: "from-purple-500 to-pink-500",
      metric: "Central Location"
    },
    {
      icon: Briefcase,
      title: "SACCO Integration",
      description: "Startup kits provided as loans through our Savings and Credit Cooperative",
      color: "from-orange-500 to-red-500",
      metric: "Business Support"
    }
  ];

  const impactAreas = [
    {
      icon: BookOpen,
      title: "Literacy Promotion",
      description: "Creating books and educational materials to improve community literacy rates"
    },
    {
      icon: DollarSign,
      title: "Income Generation",
      description: "Enabling sustainable income through bookmaking businesses and entrepreneurship"
    },
    {
      icon: Building,
      title: "Local Business Support",
      description: "Strengthening local economy through small business development and job creation"
    },
    {
      icon: Star,
      title: "Self-Reliance",
      description: "Fostering independence and reducing dependency through vocational skills training"
    }
  ];

  const galleryImages = [
    { src: "img/books/0-min.jpg", title: "Bookmaking Training Session" },
    { src: "img/books/1-min.jpg", title: "Hands-on Learning" },
    { src: "img/books/2-min.jpg", title: "Skill Development" },
    { src: "img/books/3-min.jpg", title: "Manufacturing Process" },
    { src: "img/books/4-min.jpg", title: "Community Workshop" },
    { src: "img/books/5-min.jpg", title: "Product Creation" },
    { src: "img/books/6-min.jpg", title: "Quality Control" },
    { src: "img/books/7-min.jpg", title: "Finished Products" },
    { src: "img/books/8-min.jpg", title: "Graduate Success" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
      <Header />
      
      {/* Hero Section */}
      <div className="relative overflow-hidden pt-40 pb-20">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-64 h-64 bg-orange-200/30 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-48 h-48 bg-amber-200/30 rounded-full blur-2xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/4 w-32 h-32 bg-yellow-200/30 rounded-full blur-xl animate-pulse delay-2000"></div>
          
          {/* Decorative book pattern */}
          <div className="absolute top-1/3 right-1/4 w-96 h-96 opacity-5">
            <svg viewBox="0 0 100 100" className="w-full h-full text-orange-600 animate-pulse">
              <rect x="30" y="20" width="40" height="60" rx="2" fill="currentColor"/>
              <rect x="32" y="25" width="36" height="2" fill="white"/>
              <rect x="32" y="30" width="36" height="2" fill="white"/>
              <rect x="32" y="35" width="36" height="2" fill="white"/>
            </svg>
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4">
          <div 
            id="hero-content"
            data-animate
            className={`text-center transform transition-all duration-1000 ${
              isVisible['hero-content'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <div className="inline-flex items-center bg-orange-100 text-orange-800 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <BookOpen className="w-4 h-4 mr-2" />
              Vocational Skills & Literacy Initiative
            </div>
            
            <h1 className="text-4xl md:text-6xl font-black mb-8">
              <span className="bg-gradient-to-r from-orange-600 via-amber-600 to-yellow-600 bg-clip-text text-transparent">
                Community Book
              </span>
              <br />
              <span className="text-gray-800">Project</span>
            </h1>
            
            <div className="max-w-4xl mx-auto">
              <div className="bg-white/80 backdrop-blur-xl rounded-3xl p-8 md:p-12 shadow-xl border border-orange-100">
                <p className="text-xl md:text-2xl text-gray-700 leading-relaxed mb-6">
                  Established in <strong className="text-orange-700">2019</strong>, our Community Book-making Project 
                  goes beyond creating books—it nurtures creativity, promotes literacy, and empowers 
                  underprivileged communities in Kayunga, focusing on youths and adolescent mothers.
                </p>
                <div className="bg-gradient-to-r from-orange-50 to-amber-50 border-l-4 border-orange-500 p-6 rounded-r-2xl">
                  <p className="text-lg text-gray-700 font-medium">
                    Through a <strong>3-month vocational course</strong>, we equip participants with 
                    valuable bookmaking skills, ensuring sustainability and self-reliance through 
                    entrepreneurship opportunities.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Project Highlights Section */}
      <div className="py-20 bg-white/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4">
          <div 
            id="highlights-header"
            data-animate
            className={`text-center mb-16 transform transition-all duration-1000 ${
              isVisible['highlights-header'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              Project <span className="bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">Highlights</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Key achievements and features of our comprehensive bookmaking initiative
            </p>
            <div className="w-32 h-1 bg-gradient-to-r from-orange-500 to-amber-500 rounded-full mx-auto mt-6"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {projectHighlights.map((highlight, index) => {
              const Icon = highlight.icon;
              return (
                <div
                  key={index}
                  id={`highlight-${index}`}
                  data-animate
                  className={`transform transition-all duration-1000 delay-${index * 200} ${
                    isVisible[`highlight-${index}`] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                  }`}
                >
                  <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-l-4 border-orange-500">
                    <div className={`w-16 h-16 bg-gradient-to-r ${highlight.color} rounded-2xl flex items-center justify-center mb-6 mx-auto`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-black text-gray-800 mb-2">{highlight.metric}</div>
                      <div className="text-lg font-semibold text-orange-600 mb-2">{highlight.title}</div>
                      <div className="text-sm text-gray-600">{highlight.description}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Training Process Section */}
      <div className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div 
            id="process-content"
            data-animate
            className={`mb-16 transform transition-all duration-1000 ${
              isVisible['process-content'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
                Training <span className="bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">Process</span>
              </h2>
              <p className="text-xl text-gray-600">From skills development to business startup</p>
            </div>

            <div className="bg-gradient-to-r from-orange-600 to-amber-600 rounded-3xl p-1 shadow-2xl">
              <div className="bg-white rounded-3xl p-8 md:p-12">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                  <div>
                    <h3 className="text-3xl font-bold text-gray-800 mb-6">
                      Comprehensive <span className="bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">Training Program</span>
                    </h3>
                    <div className="space-y-6">
                      <div className="flex items-start space-x-4">
                        <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl flex items-center justify-center flex-shrink-0">
                          <span className="text-white font-bold">1</span>
                        </div>
                        <div>
                          <h4 className="text-xl font-bold text-gray-800 mb-2">Skills Training</h4>
                          <p className="text-gray-600">3-month comprehensive bookmaking course at our Kayunga Central facility</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-4">
                        <div className="w-12 h-12 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-xl flex items-center justify-center flex-shrink-0">
                          <span className="text-white font-bold">2</span>
                        </div>
                        <div>
                          <h4 className="text-xl font-bold text-gray-800 mb-2">SACCO Support</h4>
                          <p className="text-gray-600">Startup kits provided as loans through our Savings and Credit Cooperative</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-4">
                        <div className="w-12 h-12 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
                          <span className="text-white font-bold">3</span>
                        </div>
                        <div>
                          <h4 className="text-xl font-bold text-gray-800 mb-2">Business Launch</h4>
                          <p className="text-gray-600">Establish independent bookmaking businesses for sustainable income</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-2xl p-8">
                    <div className="text-center mb-6">
                      <Award className="w-16 h-16 text-orange-600 mx-auto mb-4" />
                      <h4 className="text-2xl font-bold text-gray-800">230 Success Stories</h4>
                    </div>
                    <p className="text-gray-700 leading-relaxed text-center">
                      Our project has successfully trained <strong>230 beneficiaries</strong> from Kayunga District, 
                      creating a network of skilled entrepreneurs who are now generating sustainable income 
                      through their bookmaking businesses.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Impact Areas */}
          <div 
            id="impact-areas"
            data-animate
            className={`transform transition-all duration-1000 delay-500 ${
              isVisible['impact-areas'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <div className="text-center mb-12">
              <h3 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Community <span className="bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">Impact</span>
              </h3>
              <p className="text-xl text-gray-600">Beyond bookmaking—transforming lives and communities</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {impactAreas.map((area, index) => {
                const Icon = area.icon;
                return (
                  <div
                    key={index}
                    className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
                  >
                    <div className="flex items-start space-x-4">
                      <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-amber-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-xl font-bold text-gray-800 mb-3">{area.title}</h4>
                        <p className="text-gray-600 leading-relaxed">{area.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Gallery Section */}
      <div className="py-20 bg-gradient-to-br from-orange-50 to-amber-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              Project <span className="bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">Gallery</span>
            </h2>
            <p className="text-xl text-gray-600">Documenting skills development and entrepreneurship success</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {galleryImages.map((image, index) => (
              <div key={index} className="group relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                <img
                  src={image.src}
                  alt={image.title}
                  className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-4 left-4 text-white">
                    <div className="font-semibold">{image.title}</div>
                    <div className="text-sm text-gray-200">Community Book Project</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Call to Action Section */}
      <div className="py-20 bg-gradient-to-r from-orange-600 via-amber-600 to-yellow-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 text-center">
          <div 
            id="cta-content"
            data-animate
            className={`transform transition-all duration-1000 ${
              isVisible['cta-content'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <div className="inline-flex items-center bg-white/20 backdrop-blur-sm text-white px-6 py-3 rounded-full text-lg font-semibold mb-8">
              <BookOpen className="w-5 h-5 mr-3" />
              Learn • Create • Earn
            </div>
            
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-8">
              Empower Through Education
            </h2>
            <p className="text-xl text-white/90 mb-12 max-w-3xl mx-auto">
              Support our Community Book Project and help us continue empowering youths and adolescent mothers 
              with valuable vocational skills, entrepreneurship opportunities, and sustainable income generation 
              through bookmaking and literacy promotion.
            </p>
            
            <button 
              onClick={() => window.location.href = '/fund-our-work'}
              className="bg-white text-orange-600 px-10 py-4 rounded-2xl font-bold text-lg transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center gap-2 mx-auto"
            >
              Support Our Mission <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default Book;